// Instanced cross-plane foliage.
//
// Thousands of plants have to cost one draw call, which forces three choices:
//
// 1. One InstancedMesh. Individual meshes would be one draw call each and the
//    headset would die at a few hundred.
// 2. One atlas texture, so every species shares a material — different textures
//    would mean different materials and throw the instancing away. Each instance
//    picks its tile through an instanced attribute.
// 3. alphaTest, not transparent. Transparent foliage doesn't write depth, so
//    cards sort wrongly against each other and cost a full blended pass over
//    heavy overdraw. Alpha-testing keeps them in the opaque pass where the depth
//    buffer sorts them for free.
//
// The geometry is a cross of three fixed quads rather than a camera-facing
// billboard. Billboards pivot per eye in VR, which reads as flat cardboard the
// moment you have stereo depth — the whole reason they look fine in a monoscopic
// screenshot and wrong in a headset. Three static quads read as volume from any
// angle and cost 6 triangles.

import React, { useMemo, useRef, useLayoutEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';
import { TRUNK_HEIGHT } from './TrunkLayer';

export const ATLAS_COLS = 3;
export const ATLAS_ROWS = 2;

/** Tiles in public/textures/foliage/atlas.png, in packing order. */
export const FOLIAGE_SPECIES = [
  { id: 'grass', label: 'Grass', tile: 0, height: 0.55 },
  { id: 'canopy', label: 'Canopy', tile: 1, height: 2.60 },
  { id: 'shrub', label: 'Shrub', tile: 2, height: 1.15 },
  { id: 'flowers', label: 'Flowers', tile: 3, height: 0.70 },
  { id: 'fern', label: 'Fern', tile: 4, height: 0.95 },
  { id: 'reeds', label: 'Reeds', tile: 5, height: 1.45 },
];

export interface FoliageInstance {
  position: [number, number, number];
  /** Index into FOLIAGE_SPECIES. */
  species: number;
  scale: number;
  rotation: number;
}

/** Three quads at 60° to each other, pivoted at the base so instances sit on the ground. */
function crossPlaneGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];

  for (let p = 0; p < 3; p++) {
    const a = (p * Math.PI) / 3;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const base = p * 4;
    // corners: bottom-left, bottom-right, top-right, top-left
    const corners: [number, number][] = [[-0.5, 0], [0.5, 0], [0.5, 1], [-0.5, 1]];
    corners.forEach(([x, y]) => {
      pos.push(x * c, y, -x * s);
    });
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

export const FoliageLayer: React.FC<{
  instances: FoliageInstance[];
  /** Sway amount in metres at the tip. 0 disables the wind entirely. */
  wind?: number;
}> = ({ instances, wind = 0.07 }) => {
  const tex = useTexture('/textures/foliage/atlas.png');
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const { gl } = useThree();

  const geometry = useMemo(() => crossPlaneGeometry(), []);
  const uniforms = useMemo(
    () => ({ uTime: { value: 0 }, uWind: { value: wind } }),
    [wind],
  );

  useMemo(() => {
    tex.colorSpace = THREE.SRGBColorSpace;
    // Foliage is viewed at grazing angles across a whole field; without this the
    // distant cards shimmer.
    tex.anisotropy = Math.min(8, gl?.capabilities?.getMaxAnisotropy?.() ?? 1);
    // Atlas tiles must not bleed into each other when minified.
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;
  }, [tex, gl]);

  // Per-instance transforms and tile choice. This must be a layout effect, not
  // a memo: a memo body runs during render while the ref is still null, so it
  // bails out once and never runs again — every instance keeps the identity
  // matrix and the whole field stacks up at the origin.
  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const tiles = new Float32Array(instances.length);

    instances.forEach((inst, i) => {
      const sp = FOLIAGE_SPECIES[inst.species] ?? FOLIAGE_SPECIES[0];
      const h = sp.height * inst.scale;
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), inst.rotation);
      m.compose(
        new THREE.Vector3(...inst.position),
        q,
        new THREE.Vector3(h, h, h),
      );
      mesh.setMatrixAt(i, m);
      tiles[i] = sp.tile;
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.geometry.setAttribute('aTile', new THREE.InstancedBufferAttribute(tiles, 1));
    mesh.computeBoundingSphere();
  }, [instances, geometry]);

  useFrame((_, delta) => {
    uniforms.uTime.value += Math.min(delta, 0.1);
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, undefined, Math.max(1, instances.length)]}
      castShadow
      receiveShadow
      frustumCulled={false}
    >
      <meshStandardMaterial
        map={tex}
        // Cards are single-sided quads seen from both sides.
        side={THREE.DoubleSide}
        // 0.5 rather than a low value: the generated art has hard edges, so a
        // low cutoff only keeps semi-transparent fringe pixels.
        alphaTest={0.5}
        transparent={false}
        roughness={0.85}
        metalness={0}
        onUpdate={(self: THREE.Material) => {
          if (self.userData.__gxWired) return;
          self.userData.__gxWired = true;
          self.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, uniforms);
            shader.vertexShader = shader.vertexShader
              .replace(
                '#include <common>',
                `#include <common>
                attribute float aTile;
                uniform float uTime;
                uniform float uWind;
                varying float vTile;`,
              )
              // Pick this instance's atlas tile.
              .replace(
                '#include <uv_vertex>',
                `#include <uv_vertex>
                vTile = aTile;
                {
                  float col = mod(aTile, ${ATLAS_COLS}.0);
                  float row = floor(aTile / ${ATLAS_COLS}.0);
                  // v is flipped: atlas row 0 is the top of the image, but
                  // texture v counts up from the bottom.
                  vMapUv = vec2(
                    (col + vMapUv.x) / ${ATLAS_COLS}.0,
                    (${ATLAS_ROWS}.0 - 1.0 - row + vMapUv.y) / ${ATLAS_ROWS}.0
                  );
                }`,
              )
              // Flat quads lit by their true normals look like dark cardboard at
              // half the angles you see them from. Biasing the normal upward
              // approximates light scattering through a canopy and is what makes
              // card foliage read as soft rather than flat.
              .replace(
                '#include <beginnormal_vertex>',
                `#include <beginnormal_vertex>
                objectNormal = normalize(mix(objectNormal, vec3(0.0, 1.0, 0.0), 0.45));`,
              )
              // Wind. Scaled by uv.y so the base stays planted and only the tips
              // move; phase comes from world position so neighbours don't sway in
              // lockstep, which is what gives away a shader wind.
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                {
                  vec3 iPos = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
                  float phase = uTime * 1.4 + iPos.x * 0.7 + iPos.z * 0.7;
                  float amt = uWind * uv.y * uv.y;
                  transformed.x += sin(phase) * amt;
                  transformed.z += cos(phase * 0.8) * amt * 0.6;
                }`,
              );
          };
          self.needsUpdate = true;
        }}
      />
    </instancedMesh>
  );
};

function makeRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
}

/** Deterministic scatter for previewing — real placement comes from the brush. */
export function scatterFoliage(count: number, radius: number, seed = 1): FoliageInstance[] {
  const rnd = makeRng(seed);
  // Clustered, not uniform. Evenly spread plants read as machine-placed however
  // good the art is, because real vegetation grows in patches. Scattering around
  // a handful of centres with a square-root falloff is the cheapest fix and does
  // more for believability than any amount of extra species.
  const clusters = Math.max(3, Math.round(count / 60));
  const centres: [number, number][] = [];
  for (let i = 0; i < clusters; i++) {
    const a = rnd() * Math.PI * 2;
    const r = Math.sqrt(rnd()) * radius;
    centres.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  const out: FoliageInstance[] = [];
  // Ground cover only. Canopy is deliberately absent: at 2.6m it belongs on a
  // trunk as a tree, and scattering it as undergrowth just builds a wall of
  // leaves that hides everything else.
  const mix = [0, 0, 0, 0, 0, 3, 3, 4, 4, 2, 5];
  for (let i = 0; i < count; i++) {
    const [cx, cz] = centres[Math.floor(rnd() * centres.length)];
    const a = rnd() * Math.PI * 2;
    const spread = radius * 0.22 * Math.sqrt(rnd());
    out.push({
      position: [cx + Math.cos(a) * spread, 0, cz + Math.sin(a) * spread],
      species: mix[Math.floor(rnd() * mix.length)],
      scale: 0.75 + rnd() * 0.6,
      rotation: rnd() * Math.PI * 2,
    });
  }
  return out;
}

/**
 * A tree is a trunk mesh plus a cluster of canopy cards — not one big card.
 *
 * Canopy cards are ordinary foliage instances, so they ride in the same
 * InstancedMesh as the grass and cost no extra draw call. Several overlapping
 * cards at different heights and angles is what gives a tree silhouette depth;
 * a single card is the thing that reads as cardboard from the side.
 */
export function makeTreeCanopy(
  position: [number, number, number],
  scale: number,
  seed: number,
): FoliageInstance[] {
  const rnd = makeRng(seed);
  const out: FoliageInstance[] = [];
  const n = 5 + Math.floor(rnd() * 3);
  const trunkH = TRUNK_HEIGHT * scale;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.8;
    const r = (0.18 + rnd() * 0.42) * scale;
    out.push({
      position: [
        position[0] + Math.cos(a) * r,
        // Cards are pivoted at their base, so drop them by most of their own
        // height to sit the mass around the crown instead of above it.
        position[1] + trunkH - 1.15 * scale + (rnd() - 0.5) * 0.5 * scale,
        position[2] + Math.sin(a) * r,
      ],
      species: 1, // canopy
      scale: (0.62 + rnd() * 0.35) * scale,
      rotation: rnd() * Math.PI * 2,
    });
  }
  return out;
}

export default FoliageLayer;
